import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { authFetch } from '../api';
import { MyMapsList } from '../components/MyMapsList';
import './profile.css';
import { KeybindSettings } from './profile/KeybindSettings';

export function ProfilePage() {
  const { user, signInWithGoogle, signOut, refreshMe } = useAuth();
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user && !editingName) {
      setName(user.displayName ?? '');
    }
  }, [user, editingName]);

  if (!user) {
    return (
      <div className="loading-screen">
        <p className="muted">Loading...</p>
      </div>
    );
  }

  const saveName = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await authFetch('/api/me', {
        method: 'PATCH',
        json: { displayName: name },
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      await refreshMe();
      setEditingName(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-page" data-testid="profile-page">
      <h2>Profile</h2>

      <div className="card profile-card">
        <div className="profile-row">
          <strong>
            <label htmlFor="profile-name-field">Display name</label>
          </strong>
          {editingName ? (
            <div className="edit-row">
              <input
                id="profile-name-field"
                type="text"
                value={name}
                maxLength={32}
                onChange={(e) => setName(e.target.value)}
                placeholder="Pick a name"
                data-testid="profile-name-input"
              />
              <button type="button" className="btn btn-primary" onClick={() => void saveName()} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setEditingName(false);
                  setName(user.displayName ?? '');
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="edit-row">
              <span>{user.displayName ?? <em className="faint">unset</em>}</span>
              <button type="button" className="btn btn-ghost" onClick={() => setEditingName(true)} data-testid="edit-name">
                Edit
              </button>
            </div>
          )}
        </div>

        <div className="profile-row">
          <strong>Account</strong>
          <span>
            {user.isAnonymous ? (
              <em>Anonymous guest</em>
            ) : (
              <>{user.email ?? user.uid.slice(0, 8)}</>
            )}
          </span>
        </div>

        <div className="profile-row">
          <strong>UID</strong>
          <code className="profile-uid">{user.uid}</code>
        </div>

        <div className="profile-row">
          <strong>Audio offset</strong>
          <span>
            {user.audioOffsetMs > 0 ? '+' : ''}
            {user.audioOffsetMs} ms · <Link to="/calibrate">recalibrate</Link>
          </span>
        </div>

        {error && <div className="error-box">{error}</div>}
      </div>

      <div className="card">
        <MyMapsList variant="profile" />
      </div>

      <KeybindSettings />

      <div className="card">
        <h3>Account actions</h3>
        {user.isAnonymous ? (
          <>
            <p className="muted">
              You're playing as an anonymous guest. Link a Google account to keep your runs across
              devices.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setError(null);
                void signInWithGoogle().catch((err: unknown) => {
                  setError(err instanceof Error ? err.message : 'Sign in failed');
                });
              }}
              data-testid="link-google"
            >
              Sign in with Google
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              void signOut();
            }}
            data-testid="sign-out"
          >
            Sign out
          </button>
        )}
      </div>
    </div>
  );
}
